import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Mail, Phone, MapPin, CheckCircle2 } from "lucide-react";

const WEB3FORMS_KEY = "cf8a9738-64ab-4f9f-823e-a169495645c9";

const empty = {
  name: "",
  email: "",
  company: "",
  role: "",
  phone: "",
  topic: "",
  message: "",
};

const topics = [
  "Führungskräfte-Coaching",
  "Vertriebstrainings",
  "Workshops",
  "Entwicklungsprogramme",
  "Sonstiges",
];

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || form.message.trim().length < 5) {
      toast.error("Bitte Name, E-Mail und eine kurze Nachricht angeben.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        access_key: WEB3FORMS_KEY,
        subject: `Neue Anfrage über mhorgasconsult.com${form.topic ? ` — ${form.topic}` : ""}`,
        from_name: "mhorgasconsult.com",
        name: form.name,
        email: form.email,
        company: form.company || "—",
        role: form.role || "—",
        phone: form.phone || "—",
        topic: form.topic || "—",
        message: form.message,
        botcheck: "",
      };
      const res = await axios.post(
        "https://api.web3forms.com/submit",
        payload,
        { headers: { "Content-Type": "application/json", Accept: "application/json" } }
      );
      if (res.data?.success) {
        setDone(true);
        setForm(empty);
        toast.success("Vielen Dank — Ihre Nachricht ist eingegangen.");
      } else {
        throw new Error(res.data?.message || "Übermittlung fehlgeschlagen.");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Übermittlung fehlgeschlagen. Bitte später erneut versuchen.";
      toast.error(typeof msg === "string" ? msg : "Fehler beim Senden.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="section-padding bg-white border-b border-black/10"
    >
      <div className="max-w-[1440px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="overline mb-4">Kontakt</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl md:text-5xl tracking-tight leading-[1.05]">
              Lassen Sie uns
              <br />
              <span className="text-[#D30000]">in Ruhe</span> sprechen.
            </h2>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-[#4A4A52] max-w-lg">
              30 Minuten, vertraulich, ohne Verkaufs­druck. Ich höre zu,
              ordne die Situation ein und sage Ihnen ehrlich, ob ich die
              richtige Begleitung bin.
            </p>

            <ul className="mt-10 space-y-5">
              <ContactItem icon={Mail} label="E-Mail" value="michaelhorgas@mhorgasconsult.com" />
              <ContactItem icon={Phone} label="Telefon" value="+49 1525 1678724" />
              <ContactItem icon={MapPin} label="Standort" value="Hamburg" />
            </ul>

            <div className="mt-12 border-t border-black/10 pt-8">
              <div className="text-xs uppercase tracking-[0.2em] text-[#4A4A52] mb-3">
                Antwortzeit
              </div>
              <p className="font-display text-xl tracking-tight">
                In der Regel innerhalb von 24 Stunden — werktags.
              </p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="border border-black/10 p-8 md:p-12 bg-white">
              {done ? (
                <div
                  className="flex flex-col items-start gap-5 py-8"
                  data-testid="contact-success"
                >
                  <CheckCircle2 size={40} className="text-[#D30000]" />
                  <h3 className="font-display font-bold text-2xl md:text-3xl tracking-tight">
                    Nachricht eingegangen.
                  </h3>
                  <p className="text-[#4A4A52] max-w-md">
                    Vielen Dank für Ihre Nachricht. Ich melde mich
                    persönlich, in der Regel innerhalb von 24 Stunden.
                  </p>
                  <button
                    onClick={() => setDone(false)}
                    className="btn-secondary mt-2"
                    data-testid="contact-new-message"
                  >
                    Neue Nachricht senden
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field label="Name" required>
                      <input
                        className="input-field"
                        value={form.name}
                        onChange={update("name")}
                        placeholder="Vor- und Nachname"
                        data-testid="contact-input-name"
                        required
                      />
                    </Field>
                    <Field label="E-Mail" required>
                      <input
                        type="email"
                        className="input-field"
                        value={form.email}
                        onChange={update("email")}
                        placeholder="ihre.adresse@institut.de"
                        data-testid="contact-input-email"
                        required
                      />
                    </Field>
                    <Field label="Haus / Unternehmen">
                      <input
                        className="input-field"
                        value={form.company}
                        onChange={update("company")}
                        placeholder="Sparkasse / Leasinggesellschaft"
                        data-testid="contact-input-company"
                      />
                    </Field>
                    <Field label="Position">
                      <input
                        className="input-field"
                        value={form.role}
                        onChange={update("role")}
                        placeholder="z. B. Bereichsleitung Vertrieb"
                        data-testid="contact-input-role"
                      />
                    </Field>
                    <Field label="Telefon">
                      <input
                        className="input-field"
                        value={form.phone}
                        onChange={update("phone")}
                        placeholder="optional"
                        data-testid="contact-input-phone"
                      />
                    </Field>
                    <Field label="Thema">
                      <select
                        className="input-field bg-white"
                        value={form.topic}
                        onChange={update("topic")}
                        data-testid="contact-input-topic"
                      >
                        <option value="">Bitte wählen…</option>
                        {topics.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </Field>
                  </div>

                  <Field label="Nachricht" required>
                    <textarea
                      className="input-field min-h-[140px] resize-y"
                      value={form.message}
                      onChange={update("message")}
                      placeholder="Worum geht es konkret? Aktuelle Situation, Ziel, Zeitrahmen…"
                      data-testid="contact-input-message"
                      required
                    />
                  </Field>

                  <p className="text-xs text-[#4A4A52]">
                    Mit dem Absenden willigen Sie der vertraulichen
                    Bearbeitung Ihrer Angaben gemäß Datenschutz­erklärung
                    ein. Keine Weitergabe an Dritte.
                  </p>

                  <button
                    type="submit"
                    className="btn-primary w-full md:w-auto"
                    disabled={submitting}
                    data-testid="contact-submit-button"
                  >
                    {submitting ? "Wird gesendet…" : "Nachricht senden"}
                    {!submitting && <ArrowRight size={18} />}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.18em] text-[#4A4A52] mb-2">
        {label}
        {required && <span className="text-[#D30000]"> *</span>}
      </span>
      {children}
    </label>
  );
}

function ContactItem({ icon: Icon, label, value }) {
  return (
    <li className="flex items-start gap-4">
      <span className="w-10 h-10 border border-black/15 flex items-center justify-center shrink-0">
        <Icon size={16} strokeWidth={1.5} />
      </span>
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-[#4A4A52]">
          {label}
        </div>
        <div className="font-medium text-[#0A0A0A] mt-1">{value}</div>
      </div>
    </li>
  );
}
